<template>
    <table class="module-table">
        <renderHead :option="option"></renderHead>
        <renderBody :data="data"></renderBody>
    </table>
</template>

<script lang="tsx" setup>
//* 组件库
import { UiIcon, UiTooltip } from "various-ui";
//* 组件属性
import type { ModuleData, ModuleTableOption } from "../module2/index";
//* 样式
import css from "./style/index.module.less";

//* 组件属性注册
defineProps<{ data: ModuleData[]; option: ModuleTableOption[] }>();

const i18n = useI18n();

const emit = defineEmits({
    spread: (data: ModuleData) => true,
});

//* 渲染表格head 内容
const renderHead = defineComponent({
    props: {
        option: {
            type: Array as PropType<ModuleTableOption[]>,
            required: true,
        },
    },
    setup(props) {
        // 作用域插槽
        const renderScopedSlot = (value: any) => {
            return {
                default: () => (
                    <div>
                        {value.name}
                        <sup v-show={value.info.diseases.length}>({value.info.diseases.length})</sup>
                    </div>
                ),
                content: () => (
                    <div class={css.module_tooltip_body}>
                        <table>
                            <tr>
                                <td>
                                    <span class="font-bold color-1">{i18n.t("Allele")}：</span>
                                </td>
                                <td>
                                    <span v-html={value.info.composition.join(", ")}></span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span class="font-bold color-1">{i18n.t("GeneticBackground")}：</span>
                                </td>
                                <td>
                                    <span v-html={value.info.background.join(" / ") || "--"}></span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span class="font-bold color-1">{i18n.t("Layout.TypeRelated", { type: "Disease" })}：</span>
                                </td>
                                <td>
                                    <span v-html={value.info.diseases.join(" / ") || "--"}></span>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <span class="font-bold color-1">{i18n.t("CellLine")}：</span>
                                </td>
                                <td>
                                    <span>{value.info.cell_lines || "--"}</span>
                                </td>
                            </tr>
                        </table>
                    </div>
                ),
            };
        };

        return () => (
            <thead>
                <tr>
                    <th>{i18n.t("Phenotypes")}</th>
                    {props.option.map((value, key) => (
                        <th key={value.name} style={{ backgroundColor: value?.color }}>
                            <UiTooltip width={475} align="center" direction="bottom" v-slots={renderScopedSlot(value)}></UiTooltip>
                        </th>
                    ))}
                </tr>
            </thead>
        );
    },
});

//* 渲染表格主体内容
const renderBody = defineComponent({
    props: {
        data: {
            type: Array as PropType<ModuleData[]>,
            required: true,
        },
    },
    setup(props) {
        return () =>
            props.data.map((value) => {
                return <renderLine data={value} level={0}></renderLine>;
            });
    },
});

//* 渲染函数表格行
const renderLine = defineComponent({
    props: {
        data: {
            type: Object as PropType<ModuleData>,
            required: true,
        },
        level: {
            type: Number,
            required: true,
        },
    },
    setup(props) {
        //* 递归渲染函数
        const renderChildren: any = (data: ModuleData, level: number) => {
            let number = 56 + (level >= 1 ? level * 16 : 0);
            return (
                <>
                    <tr class={["level" + level, data?.active && "active", "module-tr"]}>
                        <td style={{ paddingLeft: number + "px" }}>
                            <span
                                class={[data.children?.length && "multiple"]}
                                onClick={() => {
                                    methods.switch(data);
                                }}>
                                {data.name}
                                <UiIcon class="module-icon" v-show={data.children?.length} name="arrow/entity"></UiIcon>
                            </span>
                        </td>
                        {data.values.map((value) => {
                            return (
                                <td>
                                    <span class="dot" v-show={value.show == "O"}></span>
                                    <span class={"N"} v-show={value.show == "N"}>
                                        {value.show}
                                    </span>
                                </td>
                            );
                        })}
                    </tr>
                    {data.children?.length && data.active
                        ? data.children.map((value: ModuleData) => {
                              return renderChildren(value, level + 1);
                          })
                        : null}
                </>
            );
        };

        const methods = {
            //* 展开表行开关
            switch: (data: ModuleData) => {
                if (data.children) {
                    emit("spread", data);
                }
            },
        };
        //* 递归渲染表行
        return () => <tbody class="module-body">{renderChildren(props.data, 0)}</tbody>;
    },
});
</script>
